import { useWorkspaceText } from '../interface-copy';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { useNamespaceCopy } from '@/i18n/ns';
import { overlaysCopy } from '@/i18n/ns/overlays';
import editorCopy from '@/i18n/ns/workspace-editors';
import { useMemo } from 'react';
import { useCompany } from '@/company/context';
import { useWorkspace } from '../context';
import { useSkillLibrary } from '../retrieval';
import type { AgentRevision } from './api';
import {
  SCHEDULE_PRESETS,
  WEEKDAYS,
  describeSchedule,
  formSchedule,
  type ScheduleForm,
} from './schedule';

// The Setup tab's fields: the Skill, when it runs, and one-of choices.

export function SkillPicker({
  value,
  disabled,
  current,
  onChange,
}: {
  value: string;
  disabled: boolean;
  current: AgentRevision['skill'] | null;
  onChange: (id: string) => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const company = useCompany();
  const library = useSkillLibrary(
    !disabled,
    `${ws.user?.id ?? ''}\0${company.data?.company?.id ?? ''}`,
  );
  const options = useMemo(() => {
    const published = library.skills
      .filter((skill) => skill.publishedRevision !== null)
      .map((skill) => ({
        id: skill.id,
        label: `${skill.published?.name ?? skill.draft.name} · version ${skill.publishedRevision}`,
      }));
    if (current && !published.some((option) => option.id === current.id))
      published.unshift({
        id: current.id,
        label: `${current.name ?? 'Skill'}${current.publishedRevision ? ` · version ${current.publishedRevision}` : ' · not published'}`,
      });
    return published;
  }, [library.skills, current?.id, current?.name, current?.publishedRevision]);
  return (
    <label className="ws-agents-field">
      <span>{uiText('Skill')}</span>
      <select
        aria-label={uiText('Skill')}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
      <small>{uiText('The agent always uses the Skill’s published version.')}</small>
    </label>
  );
}

export function ScheduleFields({
  value,
  disabled,
  onChange,
  bare = false,
}: {
  value: ScheduleForm;
  disabled: boolean;
  onChange: (next: ScheduleForm) => void;
  /** Without its own "When it runs" label, inside a section that already says it. */
  bare?: boolean;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const scheduleCopy = useNamespaceCopy(overlaysCopy);
  const editor = useNamespaceCopy(editorCopy);
  const locale = useChatInterfaceLocale();
  const timed = ['daily', 'weekdays', 'weekly'].includes(value.preset);
  const schedule = formSchedule(value);
  return (
    <div className="ws-agents-field">
      {!bare && <span>{uiText('When it runs')}</span>}
      <div className="ws-agents-presets" role="group" aria-label={uiText('When it runs')}>
        {SCHEDULE_PRESETS.map((preset) => (
          <button
            key={preset.value}
            type="button"
            aria-pressed={value.preset === preset.value}
            disabled={disabled}
            onClick={() => onChange({ ...value, preset: preset.value })}
          >
            {
              (
                {
                  off: editor.schedulePresetOff,
                  hourly: editor.schedulePresetHourly,
                  daily: editor.schedulePresetDaily,
                  weekdays: editor.schedulePresetWeekdays,
                  weekly: editor.schedulePresetWeekly,
                  custom: editor.schedulePresetCustom,
                } as Record<string, string>
              )[preset.value]
            }
          </button>
        ))}
      </div>
      <div className="ws-agents-inline">
        {value.preset === 'weekly' && (
          <select
            aria-label={uiText('Day')}
            value={value.weekday}
            disabled={disabled}
            onChange={(event) => onChange({ ...value, weekday: Number(event.target.value) })}
          >
            {WEEKDAYS.map((_day, index) => (
              <option key={index} value={index}>
                {new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(
                  new Date(Date.UTC(2023, 0, 1 + index, 12)),
                )}
              </option>
            ))}
          </select>
        )}
        {timed && (
          <input
            type="time"
            aria-label={uiText('Time')}
            value={value.time}
            disabled={disabled}
            onChange={(event) => onChange({ ...value, time: event.target.value })}
          />
        )}
        {value.preset === 'custom' && (
          <input
            type="text"
            aria-label={uiText('Cron expression')}
            placeholder="0 8 * * 1-5"
            value={value.cron}
            disabled={disabled}
            onChange={(event) => onChange({ ...value, cron: event.target.value })}
          />
        )}
        {schedule && (
          <small>
            {describeSchedule(schedule, scheduleCopy, locale)} · {schedule.timezone}
          </small>
        )}
      </div>
    </div>
  );
}

export function Choice<T extends string>({
  label,
  value,
  options,
  disabled,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  disabled: boolean;
  onChange: (next: T) => void;
}) {
  return (
    <div className="ws-agents-presets" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          disabled={disabled}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
